/**
 * documentPdf.ts — client-side PDF generation for transaction documents.
 *
 * Mirrors the myPOS ONE approach: rasterise the on-screen A4 "paper" node with html2canvas,
 * then slice that image across A4 pages into a jsPDF. The two heavy libraries are dynamically
 * imported so they stay out of the cold app bundle and only load when a user actually exports.
 *
 * The captured node is the same DOM the print stylesheet shows, so the PDF matches the printout.
 */

const A4_WIDTH_MM  = 210;
const A4_HEIGHT_MM = 297;

async function renderCanvas(node: HTMLElement): Promise<HTMLCanvasElement> {
  const { default: html2canvas } = await import('html2canvas');
  return html2canvas(node, {
    scale: 2,               // 2× for crisp text on the raster
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    windowWidth: node.scrollWidth,
  });
}

async function buildPdf(node: HTMLElement) {
  const { default: jsPDF } = await import('jspdf');
  const canvas = await renderCanvas(node);
  // JPEG, not PNG: a full-page A4 raster as PNG runs to tens of MB and blows the email attachment
  // limit. JPEG at high quality is ~10× smaller and indistinguishable for a black-on-white document.
  const imgData = canvas.toDataURL('image/jpeg', 0.92);

  const pdf = new jsPDF('p', 'mm', 'a4');
  const imgHeight = (canvas.height * A4_WIDTH_MM) / canvas.width;

  let heightLeft = imgHeight;
  let position = 0;
  pdf.addImage(imgData, 'JPEG', 0, position, A4_WIDTH_MM, imgHeight);
  heightLeft -= A4_HEIGHT_MM;

  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'JPEG', 0, position, A4_WIDTH_MM, imgHeight);
    heightLeft -= A4_HEIGHT_MM;
  }
  return pdf;
}

/** Generate the PDF and trigger a browser download. */
export async function savePaperPdf(node: HTMLElement, fileBaseName: string): Promise<void> {
  const pdf = await buildPdf(node);
  pdf.save(`${fileBaseName}.pdf`);
}

/**
 * Generate the PDF and return its raw base64 (no data-URI prefix) plus the filename, for
 * attaching to an email. Kept separate from savePaperPdf so the email path never triggers a
 * Save-As dialog.
 */
export async function capturePaperPdfBase64(
  node: HTMLElement,
  fileBaseName: string,
): Promise<{ base64: string; filename: string }> {
  const pdf = await buildPdf(node);
  const dataUri = pdf.output('datauristring');          // data:application/pdf;base64,XXXX
  const base64 = dataUri.slice(dataUri.indexOf(',') + 1);
  return { base64, filename: `${fileBaseName}.pdf` };
}
